import { Integration, Integrations } from '../models/Integrations';
import { JiraExport } from '../jira/models/JiraExport';
import { JiraExportMapper } from '../jira/mappers/JiraExportMapper';
import { JiraIssueCollector } from '../jira/models/JiraIssueCollector';
import { JiraIssueCollectorMapper } from '../jira/mappers/JiraIssueCollectorMapper';
import { OrganizationIntegration } from '../../common/enums/OrganizationIntegration';
import { Tanium } from '../tanium/models/Tanium';
import { TaniumMapper } from '../tanium/mappers/TaniumMapper';
import { Venafi } from '../venafi/models/Venafi';
import { VenafiMapper } from '../venafi/mappers/VenafiMapper';
import { CrowdStrike } from '../crowdstrike/models/CrowdStrike';
import { CrowdStrikeMapper } from '../crowdstrike/mappers/CrowdStrikeMapper';
import { ServiceNowMapper } from '../service-now/mappers/ServiceNowMapper';
import { ServiceNowIntegration } from '../service-now/models/ServiceNowIntegration';

const extractIntegration = <TIntegration extends Integration, TClass extends new (...args: any[]) => TIntegration>(
  Klass: TClass,
  integrations: Integration[]
) => {
  return integrations.find((i: Integration): i is TIntegration => i instanceof Klass) ?? null;
};

export class IntegrationsMapper {
  private readonly jiraIssueCollectorMapper = new JiraIssueCollectorMapper();
  private readonly jiraExportMapper = new JiraExportMapper();
  private readonly venafiMapper = new VenafiMapper();
  private readonly taniumMapper = new TaniumMapper();
  private readonly crowdStrikeMapper = new CrowdStrikeMapper();
  private readonly serviceNowMapper = new ServiceNowMapper();

  private mapToIntegration(integration: { __typename: OrganizationIntegration }) {
    switch (integration.__typename) {
      case OrganizationIntegration.JIRA:
        return this.jiraExportMapper.mapGQLModel(integration);
      case OrganizationIntegration.JIRA_ISSUE_COLLECTOR:
        return this.jiraIssueCollectorMapper.mapGQLModel(integration);
      case OrganizationIntegration.VENAFI:
        return this.venafiMapper.mapGQLModel(integration);
      case OrganizationIntegration.CROWDSTRIKE:
        return this.crowdStrikeMapper.mapGQLModel(integration);
      case OrganizationIntegration.TANIUM:
        return this.taniumMapper.mapGQLModel(integration);
      case OrganizationIntegration.SERVICE_NOW:
        return this.serviceNowMapper.mapGQLModel(integration);
      default:
        return null;
    }
  }

  mapGQLModel(data: any): Integrations {
    const integrationsData = data ?? [];
    const integrations = integrationsData.map((integration: any) => this.mapToIntegration(integration)).filter(Boolean);

    return new Integrations(
      extractIntegration(JiraExport, integrations),
      extractIntegration(JiraIssueCollector, integrations),
      extractIntegration(Venafi, integrations),
      extractIntegration(Tanium, integrations),
      extractIntegration(CrowdStrike, integrations),
      extractIntegration(ServiceNowIntegration, integrations)
    );
  }
}
